import {
  IonItem,
  IonLabel,
  IonButton,
  IonModal,
  IonContent,
  IonSearchbar,
  IonList,
  IonCheckbox,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonText,
  IonIcon,
} from "@ionic/react";
import { Controller } from "react-hook-form";
import type { Control, FieldValues, FieldPath } from "react-hook-form";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { getProjectsPaginated, type Project } from "@/api";
import React, { useState, useMemo } from "react";
import "./SelectProject.css";
import { fullProjectName } from "@/utils.ts";
import { AppHeader } from "./AppHeader";
import { alertCircleOutline, addCircleOutline } from "ionicons/icons";
import { Did } from "@/types";
import CreateProjectModal from "./Modals/CreateProjectModal";

// Special constant to represent "unknown project"
export const UNKNOWN_PROJECT_ID = null;

// Props for the input component
interface SelectProjectInputProps {
  value?: Did | null;
  disabled?: boolean;
  onProjectSelect: (projectId: Did | null) => void;
  onProjectChange?: (project: Project | null) => void;
  selectedProject?: Project;
  className?: string;
  allowUnknown?: boolean;
}

// Props for the control wrapper
interface SelectProjectControlProps<T extends FieldValues> {
  control: Control<T>;
  required: boolean;
  name: FieldPath<T>;
  disabled: boolean;
  className?: string;
  allowUnknown?: boolean;
  onProjectChange?: (project: Project | null) => void;
  selectedProject?: Project;
}

// The input component without form control logic
const SelectProjectInput: React.FC<SelectProjectInputProps> = ({
  value,
  className,
  disabled,
  onProjectSelect,
  onProjectChange,
  selectedProject: selectedProjectProp,
  allowUnknown = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();

  const isUnknownSelected = value === null || value === UNKNOWN_PROJECT_ID;

  const getSelectedProject = (
    selectedId: Did | null | undefined
  ): Project | undefined => {
    if (selectedId === null || selectedId === undefined) return undefined;

    // If selectedProject is passed as prop, use it
    if (selectedProjectProp && selectedProjectProp.id === selectedId) {
      return selectedProjectProp;
    }

    // First try to find in current loaded pages
    const found = allProjects.find((p) => p.id === selectedId);
    if (found) return found;

    // If not found, check React Query cache for individual project
    const cached = queryClient.getQueryData(["project", selectedId]);
    return cached as Project | undefined;
  };

  const handleUnknownSelect = () => {
    onProjectSelect(UNKNOWN_PROJECT_ID);
    onProjectChange?.(null);
    setIsOpen(false);
    setSearchText("");
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ["projects", "paginated", searchText],
      queryFn: ({ pageParam = 1 }) =>
        getProjectsPaginated({
          page: pageParam,
          search: searchText || undefined,
        }),
      getNextPageParam: (lastPage) => {
        return lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined;
      },
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

  const allProjects = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) ?? [];
  }, [data]);

  const handleProjectSelect = (project: Project) => {
    // Cache the selected project data
    queryClient.setQueryData(["project", project.id], project);
    onProjectSelect(project.id);
    onProjectChange?.(project);
    setIsOpen(false);
    setSearchText("");
  };

  const selectedProject = getSelectedProject(value);

  const getButtonLabel = () => {
    if (isUnknownSelected) {
      return (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <IonIcon icon={alertCircleOutline} />
          Onbekend project
        </span>
      );
    }
    if (selectedProject) {
      return fullProjectName(selectedProject);
    }
    return "Selecteer project";
  };

  return (
    <IonItem className={className}>
      <IonLabel position="stacked">Project</IonLabel>
      <IonButton
        className="mb-1"
        fill="outline"
        expand="block"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
      >
        {getButtonLabel()}
      </IonButton>

      <IonModal
        isOpen={isOpen}
        onDidDismiss={() => setIsOpen(false)}
        className="select-project-modal"
      >
        <AppHeader title="Selecteer Project" onClose={() => setIsOpen(false)} />

        <IonContent>
          <div
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Fixed header area */}
            <div>
              <IonSearchbar
                value={searchText}
                onIonInput={(e) => setSearchText(e.detail.value!)}
                placeholder="Zoek project..."
              />

              <IonItem
                button
                lines="full"
                onClick={() => setShowCreateModal(true)}
              >
                <IonIcon slot="start" icon={addCircleOutline} color="primary" />
                <IonLabel color="primary">
                  <h3 className="ion-text-bold">Nieuw project toevoegen</h3>
                </IonLabel>
              </IonItem>

              {/* Currently selected project or unknown */}
              {(selectedProject || isUnknownSelected) && (
                <>
                  <IonItem color="light" lines="none">
                    <IonCheckbox
                      className="ion-margin-end"
                      slot="start"
                      checked={true}
                    />
                    <IonLabel>
                      <p className="ion-color-medium">Huidig geselecteerd</p>
                      <h3 className="ion-text-wrap ion-text-bold">
                        {isUnknownSelected ? (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <IonIcon icon={alertCircleOutline} />
                            Onbekend project
                          </span>
                        ) : (
                          fullProjectName(selectedProject!)
                        )}
                      </h3>
                    </IonLabel>
                    <IonButton
                      slot="end"
                      fill="outline"
                      size="small"
                      onClick={() => {
                        setIsOpen(false);
                        setSearchText("");
                      }}
                    >
                      Behouden
                    </IonButton>
                  </IonItem>
                  <div
                    className="ion-margin-horizontal"
                    style={{
                      height: "1px",
                      backgroundColor: "var(--ion-color-light-shade)",
                    }}
                  />
                </>
              )}
            </div>

            {/* Scrollable project list */}
            <div style={{ flex: 1, overflow: "hidden" }}>
              <IonContent scrollEvents={true}>
                <IonList>
                  {/* Unknown project option */}
                  {allowUnknown && !searchText && (
                    <IonItem
                      button
                      onClick={handleUnknownSelect}
                      color="light"
                      className="unknown-project-option"
                    >
                      <IonCheckbox
                        className="ion-margin-end"
                        slot="start"
                        checked={isUnknownSelected}
                      />
                      <IonLabel>
                        <h3
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <IonIcon icon={alertCircleOutline} />
                          Onbekend project
                        </h3>
                        <p>Ik weet niet bij welk project dit hoort</p>
                      </IonLabel>
                    </IonItem>
                  )}

                  {allProjects.map((project) => (
                    <IonItem
                      key={project.id}
                      button
                      onClick={() => handleProjectSelect(project)}
                    >
                      <IonCheckbox
                        className="ion-margin-end"
                        slot="start"
                        checked={value === project.id}
                      />
                      <IonLabel>
                        <h3>{fullProjectName(project)}</h3>
                      </IonLabel>
                    </IonItem>
                  ))}

                  {allProjects.length === 0 && !isLoading && (
                    <IonItem>
                      <IonLabel>
                        <IonText color="medium">
                          {searchText
                            ? "Geen projecten gevonden"
                            : "Geen projecten beschikbaar"}
                        </IonText>
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>

                <IonInfiniteScroll
                  onIonInfinite={async (ev) => {
                    if (hasNextPage && !isFetchingNextPage) {
                      await fetchNextPage();
                    }
                    ev.target.complete();
                  }}
                  disabled={!hasNextPage}
                >
                  <IonInfiniteScrollContent
                    loadingText="Laden..."
                    loadingSpinner="bubbles"
                  />
                </IonInfiniteScroll>
              </IonContent>
            </div>
          </div>
        </IonContent>
      </IonModal>

      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={(project) => {
          queryClient.setQueryData(["project", project.id], project);
          queryClient.invalidateQueries({
            queryKey: ["projects", "paginated"],
          });
          handleProjectSelect(project);
        }}
      />
    </IonItem>
  );
};

// The control wrapper component
const SelectProjectControl = <T extends FieldValues>({
  control,
  className,
  required,
  name,
  disabled,
  allowUnknown = true,
  onProjectChange,
  selectedProject,
}: SelectProjectControlProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: "Selecteer een project" } : {}}
      render={({ field }) => (
        <SelectProjectInput
          className={className}
          value={field.value}
          disabled={disabled}
          onProjectSelect={field.onChange}
          onProjectChange={onProjectChange}
          allowUnknown={allowUnknown}
          selectedProject={selectedProject}
        />
      )}
    />
  );
};

export default SelectProjectControl;
export { SelectProjectInput };
